<template>
  <div>
    <a class="btn text-themed go-back" @click="goBack()">
      {{ $t("flexben.general.go_back") }}
    </a>

    <!-- Main content -->
    <div v-if="!loading">
      <div class="row mb-3">
        <div class="col-12 col-xl-6 flexben-page-title">
          <h1 class="m-0">{{ $t('flexben.offers_cashClub.edit_page.edit_title') }}</h1>
        </div>
        <div class="col-12 col-xl-6 text-right">
          <button
            class="btn btn-themed px-4"
            @click="saveChanges()"
            :disabled="saving">
            <span v-if="saving" class="spinner-border spinner-border-sm mr-2"></span>
            {{ $t('flexben.general.btn.save') }}
          </button>
        </div>
      </div>

      <!-- Offer Details Card -->
      <div class="card container-bg-white mt-3">
        <div class="row pt-3 px-3">
          <div class="col-12">
            <h3 class="mt-0 pt-1 pl-0">{{ $t("flexben.general.details") }}</h3>
          </div>

        </div>

        <div class="row px-3 pb-3">
          <!-- Right column - Details -->
          <div class="col-12">
            <div class="d-flex">
              <div class="form-group">
                <span>ID</span>
                {{details.id}}
              </div>
              <div class="form-group">
                <span>{{ $t("flexben.offers_cashClub.edit_page.offer_name") }}</span>
                {{details.name}}
              </div>
              <div class="form-group">
                <span>{{ $t("flexben.offers_cashClub.edit_page.brand") }}</span>
                {{details.brandName}}
              </div>
            </div>
            <hr class="my-4"/>
            <h5 class="mb-3">{{ $t("flexben.offers_cashClub.edit_page.campaign") }}</h5>

            <!-- Campaign Code (editable) -->
            <div class="d-flex">
              <div class="form-group m-3">
                <label for="campaign_code" class="col-form-label pt-0">
                  {{ $t("flexben.offers_cashClub.edit_page.campaign_code") }}
                </label>
                <input
                  id="campaign_code"
                  type="text"
                  class="form-control"
                  v-model="formData.campaign.cod"
                  :class="{'border-danger': errorsBag.hasOwnProperty('cod')}"
                />
              </div>

              <div class="form-group m-3">
                <label for="start_from" class="col-form-label pt-0">
                  {{ $t("flexben.offers_cashClub.edit_page.start_from") }}
                </label>
                <input
                  id="start_from"
                  type="date"
                  class="form-control"
                  v-model="formData.campaign.startFrom"
                  :class="{'border-danger': errorsBag.hasOwnProperty('startFrom')}"
                />
              </div>
              <div class="form-group m-3">
                <label for="end_from" class="col-form-label pt-0">
                  {{ $t("flexben.offers_cashClub.edit_page.end_from") }}
                </label>
                <input
                  id="end_from"
                  type="date"
                  class="form-control"
                  v-model="formData.campaign.endFrom"
                  :class="{'border-danger': errorsBag.hasOwnProperty('endFrom')}"
                />
              </div>
            </div>

            <!-- Description Section -->
            <div class="form-group">
              <h5 class="mb-3">{{ $t("flexben.offers_cashClub.edit_page.description") }}</h5>

              <div class="d-flex">
                <div class="m-3">
                  <label for="description_ro" class="col-form-label pt-0">
                    {{ $t("flexben.offers.fields.description") }} - RO
                  </label>
                  <v-editor
                    id="description_ro"
                    rows="6"
                    class="form-control-textarea w-100 my-0"
                    v-model="formData.campaign.description.ro"
                    :placeholder="$t('flexben.offers.fields.description_placeholder')"
                    :class="{'border-danger': errorsBag.hasOwnProperty('campaign.description.ro')}">
                  </v-editor>
                  <p class="text-muted small mb-3">
                    {{ getTextLength(formData.campaign.description.ro) }} / 3000
                  </p>
                </div>

                <div class="m-3">
                  <label for="description_en" class="col-form-label pt-0">
                    {{ $t("flexben.offers.fields.description") }} - EN
                  </label>
                  <v-editor
                    id="description_en"
                    rows="6"
                    class="form-control-textarea w-100 my-0"
                    v-model="formData.campaign.description.en"
                    :placeholder="$t('flexben.offers.fields.description_placeholder')"
                    :class="{'border-danger': errorsBag.hasOwnProperty('campaign.description.en')}">
                  </v-editor>
                  <p class="text-muted small mb-3">
                    {{ getTextLength(formData.campaign.description.en) }} / 3000
                  </p>
                </div>
              </div>
            </div>

            <!-- Usage Instructions Section -->
            <div class="form-group">
              <h5 class="mb-3">{{ $t("flexben.offers_cashClub.edit_page.instructions") }}</h5>
              <div class="d-flex">
                <div class="m-3">
                  <label for="usage_instructions_ro" class="col-form-label pt-0">
                    {{ $t("flexben.offers.fields.usage_instructions") }} - RO
                  </label>
                  <v-editor
                    id="usage_instructions_ro"
                    rows="6"
                    class="form-control-textarea w-100 my-0"
                    v-model="formData.campaign.usage_instructions.ro"
                    :placeholder="$t('flexben.offers.fields.instructions_placeholder')"
                    :class="{'border-danger': errorsBag.hasOwnProperty('campaign.usage_instructions.ro')}">
                  </v-editor>
                  <p class="text-muted small mb-3">
                    {{ getTextLength(formData.campaign.usage_instructions.ro) }} / 3000
                  </p>
                </div>

                <div class="m-3">
                  <label for="usage_instructions_en" class="col-form-label pt-0">
                    {{ $t("flexben.offers.fields.usage_instructions") }} - EN
                  </label>
                  <v-editor
                    id="usage_instructions_en"
                    rows="6"
                    class="form-control-textarea w-100 my-0"
                    v-model="formData.campaign.usage_instructions.en"
                    :placeholder="$t('flexben.offers.fields.instructions_placeholder')"
                    :class="{'border-danger': errorsBag.hasOwnProperty('campaign.usage_instructions.en')}">
                  </v-editor>
                  <p class="text-muted small mb-3">
                    {{ getTextLength(formData.campaign.usage_instructions.en) }} / 3000
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div class="row mt-3">
        <div class="col-12 col-xl-6 flexben-page-title">

        </div>
        <div class="col-12 col-xl-6 text-right">
          <button
            class="btn btn-themed px-4"
            @click="saveChanges()"
            :disabled="saving">
            <span v-if="saving" class="spinner-border spinner-border-sm mr-2"></span>
            {{ $t('flexben.general.btn.save') }}
          </button>
        </div>
      </div>
    </div>
    <content-loader :loader-visible="this.loading" :loader-error="loaderError"></content-loader>
  </div>
</template>

<script>
import httpFlexbenService from "@/http/http-flexben";
import { VueEditor } from "vue2-editor";

export default {
  name: "CashClubOfferDetails",
  components: {
    'v-editor': VueEditor
  },
  props: {
    itemId: {
      type: [Number, null],
      required: true
    }
  },
  data() {
    return {
      loading: false,
      loaderError: false,
      saving: false,
      errorsBag: {},
      httpService: httpFlexbenService.offersCashClub,
      details: {
        id: null,
        name: '',
        brandName: '',
      },
      formData: {
        campaign: {
          cod: '',
          startFrom: '',
          endFrom: '',
          description: {
            ro: '',
            en: ''
          },
          usage_instructions: {
            ro: '',
            en: ''
          }
        }
      }
    }
  },
  created() {
    this._loadOfferDetails();
  },
  methods: {
    _loadOfferDetails() {
      this.loading = true;

      this.httpService.details(this.itemId)
        .then((response) => {
          const data = response.data.data;
          this.details = {
            id: data.id || null,
            name: data.name || '',
            brandName: data.brandName || '',
          };

          if (data.campaign) {
            this.formData.campaign.cod = data.campaign.cod;
            this.formData.campaign.startFrom = data.campaign.startFrom;
            this.formData.campaign.endFrom = data.campaign.endFrom;
            this.formData.campaign.description.ro = data.campaign.description.ro;
            this.formData.campaign.description.en = data.campaign.description.en;
            this.formData.campaign.usage_instructions.ro = data.campaign.usage_instructions.ro;
            this.formData.campaign.usage_instructions.en = data.campaign.usage_instructions.en;
          }
        })
        .catch((error) => {
          console.error('Error loading offer details:', error);
          this.loaderError = true;
        })
        .finally(() => {
          this.loading = false;
        });
    },

    saveChanges() {
      this.saving = true;
      this.errorsBag = {};

      if (!this.formData.campaign.startFrom) {
        this.errorsBag.startFrom = true;
      }

      if (!this.formData.campaign.endFrom) {
        this.errorsBag.endFrom = true;
      }

      if (Object.keys(this.errorsBag).length > 0) {
          this.saving = false;
          return;
      }

      const updateData = {
        id: this.itemId,
        campaign: {
          cod: this.formData.campaign.cod,
          startFrom: this.formData.campaign.startFrom,
          endFrom: this.formData.campaign.endFrom,
          description: {
            ro: this.formData.campaign.description.ro,
            en: this.formData.campaign.description.en
          },
          usage_instructions: {
            ro: this.formData.campaign.usage_instructions.ro,
            en: this.formData.campaign.usage_instructions.en
          }
        }
      };

      this.httpService.updateFields(updateData)
        .then((response) => {
          this.resetForm();
        })
        .catch((error) => {
          if (error.response && error.response.status === 400) {
            this.errorsBag = error.response.data.error_fields || {};
          } else {
            console.error('Error updating offer:', error);
            this.errorsBag = {
              general: [this.$t('flexben.general.error_saving')]
            };
          }
        })
        .finally(() => {
          this.saving = false;
        });
    },

    resetForm() {
      this.errorsBag = {};
      this.details = {
        id: null,
          name: '',
          brandName: '',
      };
      this.formData = {
        campaign: {
          cod: '',
          startFrom: '',
          endFrom: '',
          description: {
            ro: '',
            en: ''
          },
          usage_instructions: {
            ro: '',
            en: ''
          }
        }
      }
      this.$emit('clearItemId', true);
    },

    /**
     * Go back to list view
     */
    goBack() {
      this.$emit('clearItemId', true);
    },
    getTextLength(text) {
      return text ? text.length : 0;
    }
  }
}
</script>

<style scoped>
.go-back {
  cursor: pointer;
  text-decoration: none;
}

.go-back:hover {
  text-decoration: underline;
}
</style>
